import { useCallback } from 'react';
import type { DeselectOptionActionMeta, SelectOptionActionMeta } from 'react-select';

import type { FilterOption } from '../../../../../apis/graphql/types';
import type { DropdownOption } from '../../../../../common/Dropdown';
import { ActionTypes } from '../../../../../common/MultiSelectDropdown';
import type { MultiSelectValue } from '../../../../../common/MultiSelectDropdown/MultiSelectDropdown';

type Params = {
  id: string;
  value: FilterOption[];
  options: { label: string; value: string }[];
  onChange: (id: string, value: FilterOption[]) => void;
  onResetOptions?: () => void;
};

export type UseMultiSelectHandlers = {
  selectedDropdownOptions: DropdownOption[];
  handleChange: (
    _: MultiSelectValue,
    meta: SelectOptionActionMeta<DropdownOption> | DeselectOptionActionMeta<DropdownOption>
  ) => void;
  handleSelectAll: () => void;
  handleClearAll: () => void;
};

export const useMultiSelectHandlers = ({ id, value, options, onChange }: Params): UseMultiSelectHandlers => {
  const selectedDropdownOptions: DropdownOption[] = value.map((o) => ({ label: o.label, value: o.value }));

  const handleChange = useCallback(
    (
      _: MultiSelectValue,
      meta: SelectOptionActionMeta<DropdownOption> | DeselectOptionActionMeta<DropdownOption>
    ): void => {
      if (meta.action === ActionTypes.select && meta.option) {
        const next: FilterOption[] = [...value, { value: meta.option.value, label: meta.option.label }];
        onChange(id, next);
      }
      if (meta.action === ActionTypes.deselect && meta.option) {
        const next = value.filter((o) => o.value !== meta.option?.value);
        onChange(id, next);
      }
    },
    [id, onChange, value]
  );

  const handleSelectAll = useCallback((): void => {
    const existingValues = new Set(value.map((o) => o.value));
    const toAdd: FilterOption[] = options
      .filter((o) => !existingValues.has(o.value))
      .map((o) => ({ value: o.value, label: o.label }));
    onChange(id, [...value, ...toAdd]);
  }, [id, onChange, options, value]);

  const handleClearAll = useCallback((): void => {
    const visibleValues = new Set(options.map((o) => o.value));
    const remaining = value.filter((o) => !visibleValues.has(o.value));
    onChange(id, remaining);
  }, [id, onChange, options, value]);

  return { selectedDropdownOptions, handleChange, handleSelectAll, handleClearAll };
};
